import { useManufacturers } from '@/resources/manufacturer/manufacturerHandler';
import { useIpnBulk, useOtsPartBulk } from '@/resources/part/partHandler';
import { useSuppliers } from '@/resources/supplier/supplierHandler';
import { assertPresent, groupBy, transEnum } from '@luminovo/commons';
import {
    Incoterms,
    ManufacturerDTO,
    OtsComponentFull,
    OtsFullPart,
    Packaging,
    RegionsEnum,
    StandardPartTypes,
    SupplierDTO,
    TotalCostOfOwnershipRuleConditionType,
    TotalCostOfOwnershipRuleDTO,
} from '@luminovo/http-client';
import {
    formatPackaging,
    formatPart,
    totalCostOfOwnershipRuleConditionTypeTranslations,
} from '@luminovo/sourcing-core';
import React from 'react';

export type TcoRuleFormState = TotalCostOfOwnershipRuleDTO;

type InlinedCondition =
    | { condition_type: TotalCostOfOwnershipRuleConditionType.Packaging; value: Packaging }
    | { condition_type: TotalCostOfOwnershipRuleConditionType.CountryOfOrigin; value: RegionsEnum }
    | { condition_type: TotalCostOfOwnershipRuleConditionType.Incoterms; value: Incoterms }
    | {
          condition_type: TotalCostOfOwnershipRuleConditionType.CustomsCode;
          value: { code_type: 'Hts' | 'Taric'; code: string };
      }
    | {
          condition_type: TotalCostOfOwnershipRuleConditionType.PartNumber;
          value: { type: 'OffTheShelf'; data: OtsFullPart } | { type: 'Ipn'; data: OtsComponentFull };
      }
    | { condition_type: TotalCostOfOwnershipRuleConditionType.Supplier; value: SupplierDTO }
    | { condition_type: TotalCostOfOwnershipRuleConditionType.Manufacturer; value: ManufacturerDTO };

export function formatConditions(conditions: InlinedCondition[]): string {
    const groupedConditions = groupBy(conditions, (condition) => condition.condition_type);

    return Object.entries(groupedConditions)
        .map(
            ([type, conditions]) =>
                `${transEnum(
                    type as TotalCostOfOwnershipRuleConditionType,
                    totalCostOfOwnershipRuleConditionTypeTranslations,
                )}: ${conditions.map((c) => formatCondition(c)).join(`, `)}`,
        )
        .join(` AND `);
}

function formatCondition(condition: InlinedCondition): string {
    switch (condition.condition_type) {
        case TotalCostOfOwnershipRuleConditionType.Packaging:
            return formatPackaging(condition.value);

        case TotalCostOfOwnershipRuleConditionType.CountryOfOrigin:
            return condition.value;

        case TotalCostOfOwnershipRuleConditionType.CustomsCode:
            return `${condition.value.code_type.toLocaleUpperCase()} ${condition.value.code}`;

        case TotalCostOfOwnershipRuleConditionType.PartNumber:
            return formatPart(condition.value.data);

        case TotalCostOfOwnershipRuleConditionType.Supplier:
            return condition.value.name;

        case TotalCostOfOwnershipRuleConditionType.Incoterms:
            return condition.value;
        case TotalCostOfOwnershipRuleConditionType.Manufacturer:
            return condition.value.name;
    }
}

function inlineRule({
    rule,
    otsParts,
    ipns,
    suppliers,
    manufacturers,
}: {
    rule: TcoRuleFormState;
    otsParts: OtsFullPart[];
    ipns: OtsComponentFull[];
    suppliers: SupplierDTO[];
    manufacturers: ManufacturerDTO[];
}): TotalCostOfOwnershipRule {
    return {
        ...rule,
        inlinedConditions: rule.conditions.map((condition): InlinedCondition => {
            switch (condition.condition_type) {
                case TotalCostOfOwnershipRuleConditionType.Packaging:
                case TotalCostOfOwnershipRuleConditionType.CountryOfOrigin:
                case TotalCostOfOwnershipRuleConditionType.CustomsCode:
                case TotalCostOfOwnershipRuleConditionType.Incoterms:
                    return condition;
                case TotalCostOfOwnershipRuleConditionType.Supplier:
                    return {
                        ...condition,
                        value: assertPresent(suppliers?.find((s) => s.id === condition.value)),
                    };
                case TotalCostOfOwnershipRuleConditionType.Manufacturer:
                    return {
                        ...condition,
                        value: assertPresent(manufacturers?.find((m) => m.id === condition.value)),
                    };
                case TotalCostOfOwnershipRuleConditionType.PartNumber:
                    if (condition.value.type === StandardPartTypes.OffTheShelf) {
                        return {
                            ...condition,
                            value: {
                                type: 'OffTheShelf',
                                data: assertPresent(otsParts?.find((p) => p.id === condition.value.data)),
                            },
                        };
                    } else if (condition.value.type === StandardPartTypes.Ipn) {
                        return {
                            ...condition,
                            value: {
                                type: 'Ipn',
                                data: assertPresent(ipns?.find((p) => p.id === condition.value.data)),
                            },
                        };
                    }
                    throw new Error('Invalid part type');
                default:
                    return condition;
            }
        }),
    };
}

function extractPartIds(rules: TcoRuleFormState[] | undefined, partType: StandardPartTypes) {
    return rules?.flatMap((rule) =>
        rule.conditions.flatMap((condition) =>
            condition.condition_type === TotalCostOfOwnershipRuleConditionType.PartNumber &&
            condition.value.type === partType
                ? [condition.value.data]
                : [],
        ),
    );
}

export type TotalCostOfOwnershipRule = TcoRuleFormState & { inlinedConditions: InlinedCondition[] };

export function useTotalCostOfOwnershipRules(rules: TotalCostOfOwnershipRuleDTO[] | undefined): {
    data: TotalCostOfOwnershipRule[] | undefined;
    isLoading: boolean;
} {
    const otsPartIds = React.useMemo(() => extractPartIds(rules, StandardPartTypes.OffTheShelf), [rules]);
    const ipnIds = React.useMemo(() => extractPartIds(rules, StandardPartTypes.Ipn), [rules]);

    const { data: suppliers } = useSuppliers();
    const { data: manufacturers } = useManufacturers();
    const { data: otsParts } = useOtsPartBulk(otsPartIds, { type: 'OutsideRfQ' });
    const { data: ipns } = useIpnBulk(ipnIds, { type: 'OutsideRfQ' });

    return React.useMemo(() => {
        if (!rules || !suppliers || !otsParts || !ipns || !manufacturers) {
            return { data: undefined, isLoading: true };
        }

        const inlinedRules = rules.map((rule) =>
            inlineRule({
                rule,
                otsParts,
                ipns,
                suppliers,
                manufacturers,
            }),
        );
        return { data: inlinedRules, isLoading: false };
    }, [otsParts, ipns, rules, suppliers, manufacturers]);
}
